import { immer } from "zustand/middleware/immer";
import type { ChatMessage } from "~/components/AiChat";
import { create } from "zustand";
import { subscribeWithSelector } from "zustand/middleware";
import { devtools } from "zustand/middleware";
import { createSelectors } from "./createSelectorsFunction";
import { loadMessagesByHistoryId } from "~/services/chat.services";
import {
  hasActiveChatSession,
  mergeChatMessages,
  normalizeProjectId,
  projectIdsMatch,
} from "~/lib/chatMessageSync";

type ChatMessageState = {
  messages: ChatMessage[];
  chatHistory: any[];
  historyId: string;
  latestGeneration: string;
  currentPage: number;
  hasMore: boolean;
  isLoadingHistory: boolean;
  messagesLoaded: boolean;
  isGeneratingCode: boolean;
  isUpdatingCode: boolean;
  isWaitingAnswer: boolean;
  isEditorVisible: boolean;
  hasCode: boolean;
  hasGeneratedCode: boolean; // Nuovo stato per tracciare se è stata fatta almeno una generazione
  userToken: string;
  isUserLoggedIn: boolean;
  invokedTool: string;
  loadingStatusMessage: string;
  resetScrollButton: boolean;
  hasReceivedAssistantResponse: boolean; // Track if assistant response received before showing code generation
  pendingCodeGeneration: boolean; // Track if code generation is pending (waiting for assistant response)
  actions: {
    loadMessagesByHistoryId: (
      historyId: number,
      from: string,
      appendChat: boolean,
      pageToLoad?: number
    ) => Promise<void>;
    setInitialMessage: (messages: ChatMessage) => void;
    addMessage: (message: ChatMessage, from: string) => void;
    /** Replace the full in-memory message list. */
    setMessages: (messages: ChatMessage[], from: string) => void;
    /** Append messages to the current list. */
    appendMessages: (messages: ChatMessage[], from: string) => void;
    /** Merge backend history without removing websocket-only messages. */
    mergeMessagesFromBackend: (messages: ChatMessage[], from: string) => void;
    /** @deprecated Use setMessages — kept as alias for compatibility. */
    addMessages: (messages: ChatMessage[], from: string) => void;
    setIsGeneratingCode: (isGeneratingCode: boolean, from: string) => void;
    /** Clear message list only (preserves session flags). */
    clearMessageList: (from: string) => void;
    clearMessages: () => void;
    setHistoryId: (historyId: string, from: string) => void;
    setIsEditorVisible: (isEditorVisible: boolean) => void;
    setHasCode: (hasCode: boolean) => void;
    setHasGeneratedCode: (hasGeneratedCode: boolean) => void; // Nuova action
    setUserToken: (userToken: string) => void;
    setIsUserLoggedIn: (isUserLoggedIn: boolean) => void;
    setIsWaitingAnswer: (isWaitingAnswer: boolean, from: string) => void;
    setInvokedTool: (toolName: string, from: string) => void;
    setLoadingStatusMessage: (message: string, from: string) => void;
    setResetScrollButton: (reset: boolean) => void;
    setHasReceivedAssistantResponse: (hasReceived: boolean) => void;
    setPendingCodeGeneration: (pending: boolean) => void;

  };
};

const initialState: Omit<ChatMessageState, "actions"> = {
  messages: [],
  chatHistory: [],
  historyId: "",
  latestGeneration: "",
  currentPage: 0,
  hasMore: false,
  isLoadingHistory: false,
  messagesLoaded: false,
  isGeneratingCode: false,
  isUpdatingCode: false,
  isWaitingAnswer: false,
  isEditorVisible: false,
  hasCode: false,
  hasGeneratedCode: false,
  userToken: "",
  isUserLoggedIn: false,
  invokedTool: "",
  loadingStatusMessage: "",
  resetScrollButton: false,
  hasReceivedAssistantResponse: false,
  pendingCodeGeneration: false,

};

const chatMessagesStore = immer<ChatMessageState>((set: any, get: any) => ({
  ...initialState,
  actions: {
    loadMessagesByHistoryId: async (
      historyId: number,
      from: string,
      appendChat: boolean = false,
      pageToLoad?: number
    ) => {
      if (historyId) {
        // If pageToLoad is provided and > 0, use it; otherwise use currentPage + 1 (but at least 1)
        const page = (pageToLoad !== undefined && pageToLoad > 0) ? pageToLoad : Math.max(1, get().currentPage + 1);

        if (get().isLoadingHistory) {
          console.log("Chat Messages History loading skipped:", {
            isLoading: get().isLoadingHistory,
            hasMore: get().hasMore,
          });
          return;
        }

        const targetId = normalizeProjectId(historyId);
        const currentState = get();
        const sameProject = projectIdsMatch(currentState.historyId, targetId);
        const preserveInMemory =
          sameProject &&
          currentState.messages.length > 0 &&
          hasActiveChatSession({
            isWaitingAnswer: currentState.isWaitingAnswer,
            isGeneratingCode: currentState.isGeneratingCode,
            pendingCodeGeneration: currentState.pendingCodeGeneration,
            invokedTool: currentState.invokedTool,
          });

        if (preserveInMemory) {
          console.log(
            "[ChatMessagesStore] Skipping API reload — active session with in-memory messages:",
            { historyId: targetId, from },
          );
          set(
            { historyId: targetId, isLoadingHistory: false, messagesLoaded: true },
            false,
            "Preserve in-memory messages: " + from,
          );
          return;
        }

        set({ isLoadingHistory: true }, false, "Chat Messages History Loading");
        set({ messagesLoaded: false }, false, "messagesLoaded reset");
        try {
          const data = await loadMessagesByHistoryId(historyId, page);
          const chatHistoryList = data.chatMessageResponseList || [];
          const receivedHasMore = data.hasMore ?? false;


          let chatHistory = [...chatHistoryList];
          let messages = [
            ...chatHistoryList.map((item: any) => {
              try {
                let message: any;

                if (typeof item.message === 'string') {
                  // Try to parse as JSON
                  try {
                    message = JSON.parse(item.message);
                  } catch (parseError) {
                    // If parsing fails, treat the string as the content
                    console.warn('Failed to parse message as JSON, treating as plain text:', parseError);
                    message = {
                      content: item.message,
                      role: item.role || 'assistant'
                    };
                  }
                } else if (item.message && typeof item.message === 'object') {
                  // Already an object
                  message = item.message;
                } else {
                  // Fallback: create a message from available data
                  console.warn('Unexpected message format:', item);
                  message = {
                    content: item.message || item.content || 'No message content',
                    role: item.role || 'assistant'
                  };
                }

                // Validate and ensure required fields
                if (!message.content) {
                  console.warn('Message missing content field:', message);
                  message.content = 'No message content';
                }
                if (!message.role || (message.role !== 'user' && message.role !== 'assistant')) {
                  console.warn('Message has invalid role, defaulting to assistant:', message.role);
                  message.role = 'assistant';
                }

                return message;
              } catch (error) {
                console.error('Error processing message item:', error, item);
                // Return a fallback message
                return {
                  content: 'Error loading message',
                  role: 'assistant' as const
                };
              }
            }),
          ];

          messages = messages.reverse();

          if (appendChat) {
            chatHistory = [...chatHistoryList, ...get().chatHistory];
            messages = [...messages, ...get().messages];
          }

          if (historyId) {
            set(
              { historyId: historyId.toString() },
              false,
              "Set History Id: " + from
            );
          }

          set(
            (state: ChatMessageState) => ({
              chatHistory: chatHistory,
              messages: messages,
              currentPage: page,
              hasMore: receivedHasMore,
              isLoadingHistory: false,
              messagesLoaded: true,
            }),
            false,
            "Load Messages by History Id: " + from
          );
        } catch (error) {
          console.error("Error loading messages:", error);
          set(
            { isLoadingHistory: false },
            false,
            "Chat Messages History Loading Error"
          );
        }
      }
    },
    setInitialMessage: (message: ChatMessage) => {
      set({ messages: [message] }, false, "Add Initial Message");
    },
    addMessage: (message: ChatMessage, from: string) => {
      set(
        { messages: [...get().messages, message] },
        false,
        "Add Message: " + from
      );
    },
    setMessages: (messages: ChatMessage[], from: string) => {
      set({ messages: [...messages] }, false, "Set Messages: " + from);
    },
    appendMessages: (messages: ChatMessage[], from: string) => {
      if (messages.length === 0) return;
      set(
        { messages: [...get().messages, ...messages] },
        false,
        "Append Messages: " + from,
      );
    },
    mergeMessagesFromBackend: (messages: ChatMessage[], from: string) => {
      const merged = mergeChatMessages(get().messages, messages);
      set({ messages: merged }, false, "Merge Messages From Backend: " + from);
    },
    addMessages: (messages: ChatMessage[], from: string) => {
      get().actions.setMessages(messages, from);
    },
    clearMessageList: (from: string) => {
      set({ messages: [] }, false, "Clear Message List: " + from);
    },
    setHistoryId: (historyId: string, from: string) => {
      set({ historyId: historyId }, false, "Set History Id: " + from);
    },
    setIsGeneratingCode: (isGeneratingCode: boolean, from: string) => {
      set(
        { isGeneratingCode: isGeneratingCode },
        false,
        "Set Is Generating Code: " + isGeneratingCode + " from: " + from
      );
    },
    setIsUpdatingCode: (isUpdatingCode: boolean, from: string) => {
      set(
        { isUpdatingCode },
        false,
        "Set Is Updating Code: " + isUpdatingCode + " from: " + from
      );
    },
    setIsWaitingAnswer: (isWaitingAnswer: boolean, from: string) => {
      set(
        { isWaitingAnswer: isWaitingAnswer },
        false,
        "Set Is Waiting Answer: " + isWaitingAnswer + " from: " + from
      );
    },
    setIsEditorVisible: (isEditorVisible: boolean) => {
      set(
        { isEditorVisible },
        false,
        "Set Is Editor Visible: " + isEditorVisible
      );
    },
    setHasCode: (hasCode: boolean) => {
      set({ hasCode }, false, "Set Has Code: " + hasCode);
    },
    setHasGeneratedCode: (hasGeneratedCode: boolean) => {
      set({ hasGeneratedCode }, false, "Set Has Generated Code: " + hasGeneratedCode);
    },
    setUserToken: (userToken: string) => {
      set({ userToken }, false, "Set User Token");
    },
    setIsUserLoggedIn: (isUserLoggedIn: boolean) => {
      set(
        { isUserLoggedIn },
        false,
        "Set Is User Logged In: " + isUserLoggedIn
      );
    },
    clearMessages: () => {
      get().actions.clearMessageList("clearMessages");
      set({ historyId: "" }, false, "Clear History Id");
      set({ currentPage: 0 }, false, "Reset Current Page");
      set({ chatHistory: [] }, false, "Clear Chat History");
      set({ hasMore: false }, false, "Reset Has More");
      set({ isWaitingAnswer: false }, false, "Reset Is Waiting Answer");
      set({ isGeneratingCode: false }, false, "Reset Is Generating Code");
      set({ invokedTool: "" }, false, "Reset Invoked Tool");
      set({ loadingStatusMessage: "" }, false, "Reset Loading Status Message");
      set({ hasGeneratedCode: false }, false, "Reset Has Generated Code");
      set({ hasReceivedAssistantResponse: false }, false, "Reset Has Received Assistant Response");
      set({ pendingCodeGeneration: false }, false, "Reset Pending Code Generation");
      set({ isEditorVisible: false }, false, "Reset Is Editor Visible");
      set({ hasCode: false }, false, "Reset Has Code");
      set({ latestGeneration: "" }, false, "Reset Latest Generation");
      set({ userToken: "" }, false, "Reset User Token");
      set({ isUserLoggedIn: false }, false, "Reset Is User Logged In");
    },
    setInvokedTool: (toolName: string, from: string) => {
      set(
        { invokedTool: toolName },
        false,
        "Set Invoked Tool: " + toolName + " from: " + from
      );
    },
    setLoadingStatusMessage: (message: string, from: string) => {
      set(
        { loadingStatusMessage: message },
        false,
        "Set Loading Status Message: " + message + " from: " + from
      );
    },
    setResetScrollButton: (reset: boolean) => {
      set({ resetScrollButton: reset }, false, "Set Reset Scroll Button: " + reset);
    },
    setHasReceivedAssistantResponse: (hasReceived: boolean) => {
      set({ hasReceivedAssistantResponse: hasReceived }, false, "Set Has Received Assistant Response: " + hasReceived);
    },
    setPendingCodeGeneration: (pending: boolean) => {
      set({ pendingCodeGeneration: pending }, false, "Set Pending Code Generation: " + pending);
    },

  },
}));

const useChatMessagesStoreBase = create(
  subscribeWithSelector(
    devtools(chatMessagesStore, { name: "ChatMessagesStore" })
  )
);
const useChatMessagesStore = createSelectors(useChatMessagesStoreBase);

export default useChatMessagesStore;
export const useChatMessagesStoreActions = () =>
  useChatMessagesStore((state: any) => state.actions);
